import '../css/cvpage.css';
import { useDocumentTitle } from '../ts/useDocumentTitle';

type Experience = {
  role: string;
  company: string;
  period: string;
  location: string;
  highlights: string[];
};

type Project = {
  name: string;
  description: string;
  impact: string;
  stack: string;
};

const experiences: Experience[] = [
  {
    role: 'Lead Quest Architect',
    company: 'SideQuest Studios',
    period: '2023 — Present',
    location: 'Remote',
    highlights: [
      'Designed narrative-driven quests that increased daily active users by 38%.',
      'Led a cross-functional squad of designers, writers, and engineers through weekly sprint rituals.',
      'Authored reusable quest templates adopted by three internal teams.',
    ],
  },
  {
    role: 'Systems Designer',
    company: 'GuildForge Online',
    period: '2020 — 2023',
    location: 'Utrecht, NL',
    highlights: [
      'Rebuilt the player progression system with telemetry-informed balancing.',
      'Integrated live-ops tooling that reduced quest deployment time from weeks to days.',
      'Mentored junior designers with a focus on collaborative playtesting practices.',
    ],
  },
];

const projects: Project[] = [
  {
    name: 'Dynamic Party Matchmaking',
    description: 'Adaptive matchmaking that pairs adventurers by playstyle and narrative preference.',
    impact: 'Cut churn across early quests by 24% while boosting co-op engagement.',
    stack: 'TypeScript, Node.js, PostgreSQL, Redis',
  },
  {
    name: 'Quest Telemetry Dashboard',
    description: 'Real-time observability dashboard for quest performance and drop-off analysis.',
    impact: 'Empowered designers to iterate twice as fast on narrative beats.',
    stack: 'React, D3.js, Hasura, GraphQL',
  },
];

const skills: string[] = [
  'Narrative systems design',
  'Live-ops experimentation',
  'Player telemetry analysis',
  'Agile facilitation',
  'Interactive storytelling',
  'Cross-discipline collaboration',
];

const tools: string[] = [
  'Figma',
  'Jira',
  'Confluence',
  'Notion',
  'Unity',
  'Blender',
];

function CVPage(): JSX.Element {
  useDocumentTitle('Profile');
  return (
    <div className="cv-page">
      <div className="cv-wrapper">
        <header className="cv-header">
          <div>
            <p className="cv-eyebrow">Quest Designer</p>
            <h1 className="cv-name">Avery Nightwind</h1>
            <p className="cv-summary">
              Systems-focused narrative designer blending branching storytelling with measurable player impact.
            </p>
          </div>
          <div className="cv-contact">
            <a href="mailto:avery.nightwind@sidequest.gg">avery.nightwind@sidequest.gg</a>
            <a href="tel:+31600000000">+31 6 0000 0000</a>
            <span>Utrecht, Netherlands</span>
            <a href="https://sidequest.gg/avery" target="_blank" rel="noreferrer">
              sidequest.gg/avery
            </a>
          </div>
        </header>

        <div className="cv-content">
          <aside className="cv-sidebar">
            <section>
              <h2>Core Skills</h2>
              <ul>
                {skills.map((skill) => (
                  <li key={skill}>{skill}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2>Tools</h2>
              <ul>
                {tools.map((tool) => (
                  <li key={tool}>{tool}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2>Education</h2>
              <p className="cv-education-title">B.A. Interactive Narrative Design</p>
              <p>Utrecht University • 2016 — 2020</p>
            </section>

            <section>
              <h2>Highlights</h2>
              <ul>
                <li>Certified Scrum Product Owner (2024)</li>
                <li>2023 Dutch Game Awards – Narrative Excellence</li>
              </ul>
            </section>
          </aside>

          <main className="cv-main">
            <section>
              <h2>Experience</h2>
              {experiences.map((job) => (
                <article key={`${job.company}-${job.role}`} className="cv-experience-card">
                  <header>
                    <h3>{job.role}</h3>
                    <p className="cv-experience-meta">
                      <span>{job.company}</span>
                      <span>{job.location}</span>
                      <span>{job.period}</span>
                    </p>
                  </header>
                  <ul>
                    {job.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </section>

            <section>
              <h2>Selected Projects</h2>
              <div className="cv-project-grid">
                {projects.map((project) => (
                  <article key={project.name} className="cv-project-card">
                    <h3>{project.name}</h3>
                    <p>{project.description}</p>
                    <p className="cv-project-impact">Impact: {project.impact}</p>
                    <p className="cv-project-stack">Stack: {project.stack}</p>
                  </article>
                ))}
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}

export default CVPage;
